<template>
  <Popover v-bind="$attrs" popover-class="calendar-popover" :disabled="disabled">
    <TextInput
      class="calendar-input"
      :class="{ disabled }"
      v-bind="$attrs"
      :value="value && $d(new Date(value), 'short')"
      :icon="CalendarIcon"
      :disabled="disabled"
      show-only
    />
    <template #popover>
      <div class="pager">
        <Button class="prev" :icon="ArrowIcon" :disabled="prevDisabled" @click="prev" />
        <p class="month">
          {{
            $tc(
              'calendar/month',
              startMonth !== endMonth
                ? `${$t(`month[${startMonth}]`)}−${$t(`month[${endMonth}]`)}`
                : $t(`month[${startMonth}]`),
            )
          }}
        </p>
        <Button class="next" :icon="ArrowIcon" :disabled="nextDisabled" @click="next" />
      </div>
      <div class="calendar">
        <div v-for="day in [1, 2, 3, 4, 5, 6, 0]" :key="`day-${day}`" class="day">
          {{ $t(`day[${day}]`) }}
        </div>
        <template v-for="date in dates">
          <div
            :key="date.date"
            v-close-popover="!date.outRange && !date.disable"
            class="date"
            :class="{
              current: value === date.date,
              outRange: date.outRange,
              holiday: holidays.includes(date.date),
              disable: date.disable,
            }"
            @click="!date.outRange && !date.disable && select(date.date)"
          >
            <!-- @slot 日付セルの書き換え -->
            <slot name="date" v-bind="date" />
          </div>
        </template>
        <LoadingIndicator v-if="loading" />
      </div>
    </template>
  </Popover>
</template>

<script>
import { addDays, format, isAfter, isBefore, min, max, startOfISOWeek } from 'date-fns'
import { times } from 'lodash'
import { formatDate, today } from '@/utils/date'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import Popover, { ClosePopover } from '@/components/atoms/Popover'
import Button from '@/components/atoms/Button'
import TextInput from '@/components/atoms/TextInput'
import ArrowIcon from '@/components/icons/arrow'
import CalendarIcon from '@/components/icons/CalendarIcon'

/**
 * カレンダー入力
 */
export default {
  name: 'CalendarInput',
  components: {
    LoadingIndicator,
    Popover,
    Button,
    TextInput,
  },
  directives: {
    ClosePopover,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /** 値 (v-model) */
    value: { type: String, default: null },
    /** 選択可能な範囲（開始日） */
    minDate: { type: String, default: '2000-01-01' },
    /** 選択可能な範囲（終了日） */
    maxDate: { type: String, default: '2099-12-31' },
    /** 祝日一覧 */
    holidays: { type: Array, default: () => [] },
    /** 非活性 */
    disabled: { type: Boolean, default: false },
    /** ロード中 */
    loading: { type: Boolean, default: false },
    /** 選択可否 */
    disableDate: { type: Array, default: null },
  },
  data() {
    return {
      ArrowIcon,
      CalendarIcon,
      startDate: startOfISOWeek(this.value || today()),
    }
  },
  computed: {
    startMonth() {
      return max(this.startDate, this.minDate).getMonth()
    },
    endDate() {
      return addDays(this.startDate, 34)
    },
    endMonth() {
      return min(this.endDate, this.maxDate).getMonth()
    },
    dates() {
      return times(35, i => {
        const date = addDays(this.startDate, i)
        return {
          date: formatDate(date),
          label: format(date, date.getDate() === 1 && this.startMonth !== date.getMonth() ? 'M/D' : 'D'),
          outRange: this.isBeforeDate(date) || this.isAfterDate(date),
          disable: this.disableDate.some(v => v === formatDate(date)),
        }
      })
    },
    prevDisabled() {
      return !!this.minDate && formatDate(this.startDate) <= this.minDate
    },
    nextDisabled() {
      return !!this.maxDate && this.maxDate <= formatDate(this.endDate)
    },
  },
  mounted() {
    this.changeStartDate(this.startDate)
  },
  methods: {
    prev() {
      this.changeStartDate(addDays(this.startDate, -35))
    },
    next() {
      this.changeStartDate(addDays(this.startDate, 35))
    },
    isBeforeDate(date) {
      return this.minDate && isBefore(date, this.minDate)
    },
    isAfterDate(date) {
      return this.maxDate && isAfter(date, this.maxDate)
    },
    changeStartDate(date) {
      this.startDate = date
      /**
       * 表示開始日の変更
       * @event changeStartDate
       * @type {string}
       */
      this.$emit('changeStartDate', formatDate(date))
    },
    select(date) {
      /**
       * 日の選択
       * @event change
       * @type {string}
       */
      this.$emit('change', date)
    },
  },
}
</script>

<style lang="postcss" scoped>
.calendar-input {
  cursor: pointer;
  width: 100%;

  &.disabled {
    cursor: default;
  }

  @media screen and (min-width: 768px) {
    min-width: 344px;
    width: auto;
  }
}

.pager {
  align-items: center;
  display: flex; /* for IE */
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  padding: 16px 24px;
}

.prev {
  transform: rotate(180deg);
}

.month {
  font-size: 16px;
  font-weight: 600;
  text-align: center;
}

.calendar {
  display: grid;
  grid-template: 24px repeat(5, 1fr) / repeat(7, 1fr);
  position: relative;
  width: 392px; /* for IE */

  @supports (display: grid) {
    width: auto;
  }
}

.day,
.date {
  align-items: center;
  border-color: var(--border-color);
  border-style: solid;
  border-width: 0 1px 1px 0;
  box-sizing: border-box;
  display: flex;
  float: left; /* for IE */
  justify-content: center;
  width: 56px; /* for IE */

  &:nth-child(7n + 6) {
    background-color: var(--light-grayish-blue);
  }

  &.holiday,
  &:nth-child(7n) {
    background-color: var(--very-pale-red);
  }

  &:nth-child(7n) {
    border-right: 0;
  }

  &:nth-child(n + 36) {
    border-bottom: 0;
  }

  @supports (display: grid) {
    width: auto;
  }
}

.day {
  border-top-width: 1px;
  font-size: 12px;
  height: 24px;
}

.date {
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  min-height: 48px;

  &.outRange {
    background: var(--mostly-white-1);
    color: var(--gray);
    cursor: default;
  }

  &.disable {
    cursor: default;
  }

  &.current {
    background: var(--very-pale-yellow);
  }

  @media (min-width: 394px) {
    min-width: 56px;
  }
}
</style>

<style lang="postcss">
.calendar-popover {
  width: 100%;

  @media (min-width: 394px) {
    width: auto;
  }
}
</style>
